import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppThunk } from '../../../app/store';
import { signInUserWithEmailAndPassword,
         createUserWithEmailAndPassword,
         signOutUserWithAuthHeaders,
        validateAuthHeader, 
        patchAvatar,
        sendPasswordRedefinitionMailWithEmail,
        changePasswordWithPasswordAndToken} from '../../api/sessionAPI';
import { useSelector } from 'react-redux';
import * as REQUEST_REQUIREMENTS from '../../api/requestRequirements';
import { AuthHeaders, UserMoreInfo } from '../../#interfaces/interfaces';
import { AxiosHeaders, AxiosHeaderValue, AxiosResponseHeaders, formToJSON, RawAxiosResponseHeaders } from 'axios';
import { parseJSON } from 'date-fns';

export interface User {
  email?: string;
  provider?: string;
  uid?: string;
  id?: number;
  allowPasswordChange?: boolean;
  name?: string;
  profileImage?: string;
  nickname?: string;
  role?: string;
}

interface PatchAvatarForm {
  authHeaders: AuthHeaders;
  userNickname: string;
  patchBody: File;
}

interface PasswordRedefinitionForm {
  email: string;
  redirectUrl: string;
}

interface PasswordChangeForm {
  password: string;
  passwordConfirmation: string;
  resetPasswordToken: string;
}

export interface UserRegistered {
  email?: string;
  provider?: string;
  uid?: string;
  id?: number;
  allowPasswordChange?: boolean;
  name?: string;
  profileImage?: string;
  nickname?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserSignUpData {
  nickname: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

export interface UserSignInData {
  email: string;
  password: string;
}

export interface UserSignOutData {
  accept: string;
  accessToken: string;
  client: string;
  uid: string;
}

export interface AuthState {
  authHeaders?: AuthHeaders;
  currentUser?: User;
  error?: boolean;
  errorMessages?: string[];
  loading: boolean;
  loggedIn: any;
  provider?: string;
  userMoreInfo: UserMoreInfo;
  userRegistered?: UserRegistered;
  signUpBodyData?: UserSignUpData;
}

const initialState: AuthState = {
  currentUser: undefined,
  loggedIn: undefined,
  userRegistered: {
    allowPasswordChange: undefined,
    email: undefined,
    id: undefined,
    name: undefined,
    nickname: undefined,
    profileImage: undefined,
    provider: undefined,
    uid: undefined,
    createdAt: undefined,
    updatedAt: undefined,
  },
  userMoreInfo: {  
      name: '',
      nickname: '',
      email: '',
      phoneNumber: '',
      website: '',
      lastName: '',
      city: '',
      state: '',
      country: '',
      about: '',
      avatarUrl: '',
      cover: undefined
  },
  authHeaders: undefined,
  signUpBodyData: undefined,
  error: false,
  errorMessages: [],
  loading: false
};

export const signUpUser = createAsyncThunk(
  'session/signUpUser',
  async (payload: UserSignUpData, { rejectWithValue }) => {
    console.log('[SignUp][Thunk] iniciando registo para:', payload.email);
    const response = await createUserWithEmailAndPassword(
      payload.nickname,
      payload.email,
      payload.password,
      payload.passwordConfirmation
      );
    console.log('[SignUp][Thunk] resposta recebida — status:', response?.status, '| data:', response?.data);
    if (response?.data?.errors) {
      console.warn('[SignUp][Thunk] erros de validação:', response.data.errors);
      return rejectWithValue(response.data);
    }
    if (typeof response?.status === 'number' && response.status >= 300) {
      console.error('[SignUp][Thunk] erro de servidor — status:', response.status, '| data:', JSON.stringify(response.data));
      return rejectWithValue({ errors: { full_messages: [`Server error (${response.status}). Please try again.`] } });
    }
    console.log('[SignUp][Thunk] registo bem-sucedido');
    return response;
  }
);  

export const signInUser = createAsyncThunk(
  'session/signInUser',
  async (payload: UserSignInData, { rejectWithValue }) => {
    const response = await signInUserWithEmailAndPassword(
      payload.email,
      payload.password
      );
    
    if (response.status >= 200 && response.status < 300){
      await storeAuthHeader(response.headers);
      return response.data;
    } else {
      return rejectWithValue(response.data);
    }
  }
);

export const authenticateUserFromStorage = createAsyncThunk(
  'session/authenticateUserFromStorage',
  async (_, { rejectWithValue }) => {
    const authHeaders = localAuthHeader()

    console.log('[Auth][authenticateUserFromStorage] a iniciar validação. Headers do localStorage:', {
      accessToken: authHeaders.accessToken ? '(presente)' : '(ausente)',
      client: authHeaders.client ? '(presente)' : '(ausente)',
      uid: authHeaders.uid || '(ausente)',
    });

    const response = await validateAuthHeader(authHeaders);

    console.log('[Auth][authenticateUserFromStorage] resposta da API:', {
      status: response?.status,
      success: response?.data?.success,
      userNickname: response?.data?.data?.nickname ?? null,
    });

    if (response.status >= 200 && response.status < 300){
      console.log('[Auth][authenticateUserFromStorage] autenticação bem-sucedida. nickname:', response.data.data?.nickname);
      return response.data.data;
    } else {
      console.warn('[Auth][authenticateUserFromStorage] autenticação falhou. status:', response?.status, '| erro:', response?.data);
      return rejectWithValue(response.data);
    }
  }
)

export const signOutUser = createAsyncThunk(
  'session/signOutUser',
  async (payload: UserSignOutData, { rejectWithValue }) => {
    const response = await signOutUserWithAuthHeaders(
      payload
      );
    if (response.errors){
      return rejectWithValue(response.errors);
    }
    return response;
  }
);

export const updateAvatar = createAsyncThunk(
  'session/updateAvatar',
  async (payload: PatchAvatarForm, {rejectWithValue}) => {
      const response = await patchAvatar(
          payload.authHeaders,
          payload.userNickname,
          payload.patchBody
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)


export const sendPasswordRedefinitionMail = createAsyncThunk(
  'session/sendPasswordRedefinitionMail',
  async (payload: PasswordRedefinitionForm, {rejectWithValue}) => {
      const response = await sendPasswordRedefinitionMailWithEmail(
          payload.email,
          payload.redirectUrl
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const changePassword = createAsyncThunk(
  'session/changePassword',
  async (payload: PasswordChangeForm, {rejectWithValue}) => {
      const response = await changePasswordWithPasswordAndToken(
          payload.password,
          payload.passwordConfirmation,
          payload.resetPasswordToken
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state) => {
        state.loading = true;
        state.error =  true;
        state.errorMessages = [];
      })
      .addCase(signUpUser.fulfilled, (state, action: any) => {
        state.loading = false;
        state.userRegistered = {
          id: action.payload.id,
          email: action.payload.email
        };
      })
      .addCase(signUpUser.rejected, (state, action: any) => {
        state.loading = false;
        state.error =  true;
        state.errorMessages = action.payload?.errors ?? [];
      })
      .addCase(signInUser.pending, (state) => {
        state.loading = true;
        state.loggedIn = false;
        state.userMoreInfo.name = '';
        state.userMoreInfo.nickname = '';
        state.userMoreInfo.email = '';
        state.userMoreInfo.phoneNumber = '';
        state.userMoreInfo.website = '';
        state.userMoreInfo.lastName = '';
        state.userMoreInfo.state = '';
        state.userMoreInfo.country = '';
        state.userMoreInfo.about = '';
        state.userMoreInfo.avatarUrl = '';
        state.error =  true;
        state.errorMessages = [];
      })
      .addCase(signInUser.fulfilled, (state, action: any) => {
        const data = convertKeysToCamelCase(action.payload?.data);
        const moreInfo = data?.userMoreInfo ?? {};
        state.loading = false;
        state.loggedIn = true;
        state.currentUser = data;
        state.authHeaders = localAuthHeader();
        state.userMoreInfo.name = moreInfo.name ?? '';
        state.userMoreInfo.nickname = moreInfo.nickname ?? '';
        state.userMoreInfo.email = moreInfo.email ?? '';
        state.userMoreInfo.phoneNumber = moreInfo.phoneNumber ?? '';
        state.userMoreInfo.website = moreInfo.website ?? '';
        state.userMoreInfo.lastName = moreInfo.lastName ?? '';
        state.userMoreInfo.state = moreInfo.state ?? '';
        state.userMoreInfo.country = moreInfo.country ?? '';
        state.userMoreInfo.about = moreInfo.about ?? '';
        state.userMoreInfo.avatarUrl = moreInfo.avatarUrl ?? '';
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(signInUser.rejected, (state, action: any) => {
        state.loading = false;
        state.loggedIn = false;
        state.error = true;
        state.errorMessages = action.payload?.errors ?? [];
      })
      .addCase(signOutUser.pending, (state) => {
        state.loading = true;
        state.error = true;
      })
      .addCase(signOutUser.fulfilled, (state, action: any) => {
        state.loading = false;
        state.loggedIn = false;
        state.currentUser = undefined;
        state.authHeaders = undefined;
        state.error = false;
        state.errorMessages = [];
        removeAuthHeaders();
        localStorage.removeItem('selectedDialogues');
      })
      .addCase(signOutUser.rejected, (state, action: any) => {
        state.loading = false;
        state.error = true;
        state.errorMessages = action.payload?.errors ?? [];
      })
      .addCase(authenticateUserFromStorage.pending, (state) => {
        console.log('[Auth][sessionSlice] authenticateUserFromStorage.pending — loading=true, loggedIn mantido indefinido');
        state.loading = true;
        state.error = false;
      })
      .addCase(authenticateUserFromStorage.fulfilled, (state, action: any) => {
        const converted = convertKeysToCamelCase(action.payload);
        console.log('[Auth][sessionSlice] authenticateUserFromStorage.fulfilled — loggedIn=true, currentUser:', converted?.nickname ?? null);
        state.loading = false;
        state.loggedIn = true;
        state.authHeaders = localAuthHeader();
        state.currentUser = converted;
        state.userMoreInfo.avatarUrl = converted?.userMoreInfo?.avatarUrl ?? state.userMoreInfo.avatarUrl;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(authenticateUserFromStorage.rejected, (state, action: any) => {
        console.warn('[Auth][sessionSlice] authenticateUserFromStorage.rejected — loggedIn=false. Motivo:', action.payload);
        state.loading = false;
        state.loggedIn = false;
        state.error = true;
        state.errorMessages = action.payload?.errors ?? [];
      })
      .addCase(updateAvatar.pending, (state) => {
        state.loading = true
      })
      .addCase(updateAvatar.fulfilled, (state, action: any) => {
        state.userMoreInfo.avatarUrl = convertKeysToCamelCase(action.payload).userMoreInfo.avatarUrl
        state.loading = false
      })
      .addCase(updateAvatar.rejected, (state) => {
        state.loading = false
      })
      .addCase(sendPasswordRedefinitionMail.pending, (state) => {
        state.loading = true
      })
      .addCase(sendPasswordRedefinitionMail.fulfilled, (state, action: any) => {
        state.loading = false
      })
      .addCase(sendPasswordRedefinitionMail.rejected, (state) => {
        state.loading = false
      })
    }
});

export const sessionAuthSliceReducer = sessionSlice.reducer;
export const sessionAuthSliceActions = sessionSlice.actions;

function storeAuthHeader(headers: any) {
    localStorage.setItem('accept', REQUEST_REQUIREMENTS.ACCEPT);
    localStorage.setItem('accessToken', headers["access-token"]);
    localStorage.setItem('client', headers["client"]);
    localStorage.setItem('uid', headers["uid"]);
  }

function removeAuthHeaders() {
    localStorage.removeItem('accept');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('client');
    localStorage.removeItem('uid');
}

export function getLocalStorageAuthHeaders(){
  const authHeader = {
    accept: localStorage.getItem('accept') || '',
    accessToken: localStorage.getItem('accessToken') || '',
    client: localStorage.getItem('client') || '',
    uid: localStorage.getItem('uid') || ''
  }
  return authHeader
}

export const localAuthHeader = () =>{
  const authHeader = {
    accept: localStorage.getItem('accept') || "",
    accessToken: localStorage.getItem('accessToken') || "",
    client: localStorage.getItem('client') || "",
    uid: localStorage.getItem('uid') || "",
    'token-type': 'Bearer'
  }
  return authHeader
}

export function convertKeysToCamelCase(obj: any): any {
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      return obj.map(item => convertKeysToCamelCase(item));
    } else if (obj.constructor === Object) {
      const newObj: { [key: string]: any } = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          );
          newObj[camelCaseKey] = convertKeysToCamelCase(obj[key]);
        }
      }
      return newObj;
    }
  }
  return obj;
}